import React, { useEffect, useRef, useState } from "react";
import Resources from "../../../../types/Resources";
import { deleteAttachment, getAttachment, getAttachments, postPermitAttachment } from "../../../../apiClient";
import { showDangerDialog } from "../../../../common";
import AttachmentsModal from "./AttachmentsModal";
import { PermitDocument } from "../../../../types/permits";
import Attachment, { MalwareScanResultType } from "../../../../types/Attachment";

const attachmentStatusPollingIntervalMilliseconds = 5000;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
function FilePicker(props: { resources: Resources; storedPermit: PermitDocument; canManageAttachments: boolean }) {
    const [selectedFile, setSelectedFile] = useState<File>();
    const [storedAttachments, setStoredAttachments] = useState<Attachment[]>([]);
    const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
    const [requestInProgress, setRequestInProgress] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        async function loadAttachments() {
            if (storedAttachments.length !== 0 || !props.storedPermit) {
                return;
            }

            const attachmentsResult = await getAttachments(props.storedPermit.sequenceIdentifier);

            if (attachmentsResult.failed) {
                console.error(attachmentsResult);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetAttachmentsError"));
                return;
            }

            setStoredAttachments(attachmentsResult.value);
        }

        loadAttachments();
    }, [props.storedPermit]);

    useEffect(() => {
        if (storedAttachments.length === 0) {
            setShowAttachmentsModal(false);
        }
    }, [storedAttachments]);

    const pendingAttachmentIds = storedAttachments
        .filter(
            (attachment) =>
                attachment.isDownloadPreventionEnabled && attachment.scanResult === MalwareScanResultType.Pending,
        )
        .map((attachment) => attachment.id);
    const pendingAttachmentIdsKey = pendingAttachmentIds.join(",");

    useEffect(() => {
        if (pendingAttachmentIds.length === 0) {
            return;
        }

        let timeoutId: number;

        async function pollPendingAttachments() {
            const attachmentResults = await Promise.all(
                pendingAttachmentIds.map((attachmentId) => getAttachment(attachmentId)),
            );

            const updatedAttachments = new Map<number, Attachment>();
            attachmentResults.forEach((attachmentResult) => {
                if (attachmentResult.failed) {
                    console.error(attachmentResult.error);
                    return;
                }

                updatedAttachments.set(attachmentResult.value.id, attachmentResult.value);
            });

            setStoredAttachments((prevValue) =>
                prevValue.map((prevAttachment) =>
                    updatedAttachments.has(prevAttachment.id)
                        ? updatedAttachments.get(prevAttachment.id)!
                        : prevAttachment,
                ),
            );

            timeoutId = window.setTimeout(pollPendingAttachments, attachmentStatusPollingIntervalMilliseconds);
        }

        timeoutId = window.setTimeout(pollPendingAttachments, attachmentStatusPollingIntervalMilliseconds);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [pendingAttachmentIdsKey]);

    if (!props.storedPermit) {
        return null;
    }

    async function submitAttachmentHandler() {
        if (!selectedFile) {
            return;
        }

        const formData = new FormData();
        formData.append("FileUpload.FormFile", selectedFile);

        setRequestInProgress(true);
        const result = await postPermitAttachment(props.storedPermit.sequenceIdentifier, formData);

        if (result.failed) {
            setRequestInProgress(false);
            console.error(result.error.message);

            const errorMessage =
                result.error.status === 415
                    ? props.resources.get("InvalidFileType")
                    : result.error.status === 409
                    ? props.resources.get("DuplicateAttachmentError")?.replace("{0}", selectedFile.name)
                    : props.resources.get("AddAttachmentError");

            showDangerDialog(props.resources.get("SomethingWentWrong"), errorMessage);
            return;
        }

        const attachmentResult = await getAttachment(result.value);
        if (attachmentResult.failed) {
            console.error(attachmentResult);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetAttachmentsError"));
            return;
        }

        setRequestInProgress(false);

        setStoredAttachments((prevValue) => [...prevValue, attachmentResult.value]);
        setSelectedFile(undefined);

        if (inputRef.current) {
            inputRef.current.value = "";
        }
    }

    function selectedFileChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];

            if (file.size > MAX_FILE_SIZE) {
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("FileSizeExceedsLimit"));
                setSelectedFile(undefined);

                if (inputRef.current) {
                    inputRef.current.value = "";
                }
                return;
            }
            setSelectedFile(file);
        } else {
            setSelectedFile(undefined);
        }
    }

    async function deleteAttachmentHandler(attachment: Attachment) {
        const deleteResult = await deleteAttachment(attachment.id);

        if (deleteResult.failed) {
            console.error(deleteResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("DeleteAttachmentError"));
            return;
        }

        setStoredAttachments((prevValue) => prevValue.filter((a) => a.fullFileName !== attachment.fullFileName));
    }

    return (
        <>
            {storedAttachments.length > 0 ? (
                <button
                    className="btn btn-success ps-4 pe-4 me-2"
                    title={props.resources.get("AttachedFiles")}
                    onClick={() => setShowAttachmentsModal(true)}
                >
                    <i className="fas fa-paperclip-vertical"></i>
                </button>
            ) : null}
            {props.canManageAttachments ? (
                <>
                    <div className="input-group custom-file-button" style={{ width: "600px" }}>
                        <input
                            type="file"
                            accept=".pdf,.docx,.xlsx,.jpg,.png,.bmp,.heic"
                            className="form-control"
                            onChange={selectedFileChangeHandler}
                            ref={inputRef}
                        />
                        <button
                            className="btn btn-primary"
                            type="button"
                            disabled={!selectedFile || requestInProgress}
                            onClick={submitAttachmentHandler}
                        >
                            {props.resources.get("Upload")}
                        </button>
                    </div>
                    <span id="add-attachment-error" className="text-danger"></span>
                </>
            ) : null}
            <AttachmentsModal
                attachments={storedAttachments}
                canManageAttachments={props.canManageAttachments}
                onDelete={deleteAttachmentHandler}
                resources={props.resources}
                onHide={() => setShowAttachmentsModal(false)}
                show={showAttachmentsModal}
            />
        </>
    );
}

export default FilePicker;
